import { reactive, Ref, watch, watchEffect } from 'vue';
import { Store } from 'vuex';
import { Actions } from '@/store/enums/StoreEnums';
import { DeeplinkDestination } from '@/store/modules/Notifications/DeeplinkNotificationsModule';

export type ParamInput =
  | 'number'
  | 'text'
  | 'hash'
  | 'select'
  | 'multiselect'
  | 'auction'
  | 'date'
  | 'month';

export type LookupName =
  | 'adTypes'
  | 'adAreas'
  | 'adCategories'
  | 'tradeAreas'
  | 'tradeGroupings'
  | 'areas'
  | 'indexAreas'
  | 'auctionEvents'
  | 'indexDateRanges';

type ParamValues = Record<string, unknown>;

export interface ParamSpec {
  input: ParamInput;
  lookup?: LookupName;
  options?: Array<string> | ((values: ParamValues) => Array<string>);
  // Param this one's choices come from; it is cleared when that one changes.
  dependsOn?: string;
}

export interface DeeplinkParam {
  key: string;
  required: boolean;
  spec: ParamSpec;
}

export interface Option {
  value: string | number;
  label: string;
}

export const DEEPLINK_BASE = 'https://live-app.alhisba.com/deep-link';
// Destinations whose dateFrom can't be after dateTo.
export const DATE_RANGE_DESTINATIONS = ['deals', 'index'];
// Handled outside the per-type fields.
export const RESERVED_PARAMS = ['url', 'lang'];

// Endpoint for each lookup, or null while the param it depends on is empty.
const LOOKUP_ENDPOINTS: Record<
  LookupName,
  (values: ParamValues) => string | null
> = {
  adTypes: () => '/ads-types/all',
  adAreas: () => '/areas/findAllDdl?type=ads',
  // Same as the ads listing filter: categories of the selected ad type.
  adCategories: ({ adType }) =>
    isEmpty(adType) ? null : `/property-category/findAllDdl?adType=${adType}`,
  tradeAreas: () => '/areas/findAllDdl?type=registeredTrades',
  tradeGroupings: () => '/registered-trades-grouping/all',
  areas: () => '/areas/findAllDdl',
  // Only the areas that have the selected index type.
  indexAreas: ({ type }) =>
    isEmpty(type) ? null : `/indices/areas?type=${type}`,
  auctionEvents: () => '/auctions-events',
  // Saved date ranges of the selected index type.
  indexDateRanges: ({ type }) =>
    isEmpty(type) ? null : `/indices/price-index-date-ranges/list?type=${type}`,
};

// Views each index type can open on.
const INDEX_VIEWS: Record<string, Array<string>> = {
  'volume-index': ['total-transactions', 'volume-index'],
  'price-index': ['price-index', 'price-per-meter'],
};

// Inputs for the params each destination is known to take. Anything the
// backend lists that isn't here is shown as a plain text input.
const PARAM_SPECS: Record<string, Record<string, ParamSpec>> = {
  ads: { id: { input: 'number' } },
  'ads-list': {
    adType: { input: 'select', lookup: 'adTypes' },
    area: { input: 'select', lookup: 'adAreas' },
    category: { input: 'select', lookup: 'adCategories', dependsOn: 'adType' },
  },
  auction: { id: { input: 'auction', lookup: 'auctionEvents' } },
  'auction-property': {
    id: { input: 'hash' },
    eventId: { input: 'hash' },
  },
  cce: { id: { input: 'hash' } },
  'ive-form': { type: { input: 'select', options: ['land', 'building'] } },
  ive: { id: { input: 'hash' } },
  deals: {
    dateFrom: { input: 'date' },
    dateTo: { input: 'date' },
    area: { input: 'multiselect', lookup: 'tradeAreas' },
    category: { input: 'select', lookup: 'tradeGroupings' },
  },
  index: {
    type: { input: 'select', options: Object.keys(INDEX_VIEWS) },
    area: { input: 'select', lookup: 'indexAreas', dependsOn: 'type' },
    // One of the index's saved date ranges. Sending both dateFrom and dateTo
    // overrides it.
    dateRangeId: {
      input: 'select',
      lookup: 'indexDateRanges',
      dependsOn: 'type',
    },
    dateFrom: { input: 'date' },
    dateTo: { input: 'date' },
    category: { input: 'select', lookup: 'tradeGroupings' },
    interval: {
      input: 'select',
      options: ['monthly', 'quarterly', 'yearly'],
    },
    view: {
      input: 'select',
      options: ({ type }) => INDEX_VIEWS[type as string] || [],
      dependsOn: 'type',
    },
  },
  broker: { id: { input: 'number' } },
  packages: {
    packageType: {
      input: 'select',
      options: ['ive', 'ads', 'auctions', 'trades', 'chatbot', 'enterprise'],
    },
  },
};

export const isEmpty = (value) =>
  value === '' ||
  value === null ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0);

export const destinationParams = (
  item?: DeeplinkDestination
): Array<DeeplinkParam> => {
  if (!item) return [];
  const specs = PARAM_SPECS[item.destination] || {};
  const toParam = (required: boolean) => (key: string) => ({
    key,
    required,
    spec: specs[key] || ({ input: 'text' } as ParamSpec),
  });
  return [
    ...(item.required || []).map(toParam(true)),
    ...(item.optional || []).map(toParam(false)),
  ].filter((p) => !RESERVED_PARAMS.includes(p.key));
};

export const buildDeeplink = (
  destination: string,
  paramList: Array<DeeplinkParam>,
  values: Record<string, unknown>,
  lang: string
): string => {
  if (!destination) return '';
  const query = new URLSearchParams({ url: destination });
  paramList.forEach(({ key }) => {
    const value = values[key];
    if (isEmpty(value)) return;
    query.append(
      key,
      Array.isArray(value) ? value.join(',') : String(value).trim()
    );
  });
  query.append('lang', lang);
  // Keep comma-joined lists readable, e.g. area=12,15.
  return `${DEEPLINK_BASE}?${query.toString().replace(/%2C/gi, ',')}`;
};

/**
 * Reads a link built by buildDeeplink back into its parts. Returns null for
 * anything that isn't a DEEPLINK_BASE link with a destination.
 */
export const parseDeeplink = (
  link: string
): {
  destination: string;
  lang: string;
  params: Record<string, string>;
} | null => {
  let url: URL;
  try {
    url = new URL(link.trim());
  } catch (e) {
    return null;
  }
  if (`${url.origin}${url.pathname.replace(/\/+$/, '')}` !== DEEPLINK_BASE) {
    return null;
  }
  const destination = url.searchParams.get('url');
  if (!destination) return null;
  const params: Record<string, string> = {};
  url.searchParams.forEach((value, key) => {
    if (!RESERVED_PARAMS.includes(key)) params[key] = value;
  });
  return { destination, lang: url.searchParams.get('lang') || 'ar', params };
};

/**
 * Loads the select lists the current params need, clears a param when the
 * one it depends on changes, and turns the lists into options.
 */
export const useDeeplinkLookups = (
  store: Store<unknown>,
  translate: (text: string) => string,
  currentParams: Ref<Array<DeeplinkParam>>,
  params: Ref<ParamValues>
) => {
  // Keyed by endpoint, so each ad type's categories load once.
  const lookups = reactive<Record<string, Array<Record<string, unknown>>>>({});
  const lookupLoading = reactive<Record<string, boolean>>({});

  const lookupUrl = (spec: ParamSpec) =>
    spec.lookup ? LOOKUP_ENDPOINTS[spec.lookup](params.value) : null;

  const loadLookup = async (url: string) => {
    if (lookups[url] || lookupLoading[url]) return;
    lookupLoading[url] = true;
    const res = await store.dispatch(Actions.GET_DEEPLINK_LOOKUP, url);
    lookupLoading[url] = false;
    lookups[url] = res.data || [];
  };

  watchEffect(() => {
    currentParams.value.forEach(({ spec }) => {
      const url = lookupUrl(spec);
      if (url) loadLookup(url);
    });
  });

  watch(
    () => ({
      values: params.value,
      parents: currentParams.value.map(({ spec }) =>
        spec.dependsOn ? params.value[spec.dependsOn] : undefined
      ),
    }),
    (next, prev) => {
      // A new set of params (another type, or a link being loaded) keeps its
      // values; only a parent edited in place clears its dependents.
      if (next.values !== prev.values) return;
      currentParams.value.forEach(({ key, spec }, i) => {
        if (spec.dependsOn && next.parents[i] !== prev.parents[i]) {
          params.value[key] = spec.input === 'multiselect' ? [] : '';
        }
      });
    }
  );

  const isLookupLoading = (spec: ParamSpec) => {
    const url = lookupUrl(spec);
    return !!url && !!lookupLoading[url];
  };

  // True while the param this one depends on is still empty.
  const isWaitingOnParent = (spec: ParamSpec) =>
    !!spec.dependsOn && isEmpty(params.value[spec.dependsOn]);

  // Arabic titles, as the rest of the admin's dropdowns show.
  const itemTitle = (item: Record<string, unknown>) =>
    String(
      item.titleAr ||
        item.nameAr ||
        item.titleEn ||
        item.nameEn ||
        item.title ||
        item.name ||
        item.id
    );

  const optionLabel = (value: string) => {
    const key = `deeplinkOpt_${value}`;
    const label = translate(key);
    return label === key ? value : label;
  };

  const paramOptions = (spec: ParamSpec): Array<Option> => {
    if (spec.options) {
      const values =
        typeof spec.options === 'function'
          ? spec.options(params.value)
          : spec.options;
      return values.map((value) => ({ value, label: optionLabel(value) }));
    }
    const url = lookupUrl(spec);
    const list = (url && lookups[url]) || [];
    if (spec.lookup === 'auctionEvents') {
      return list.map((item) => {
        const date = item.eventDateTime
          ? String(item.eventDateTime).slice(0, 10)
          : '';
        const name = item.organiserName || itemTitle(item);
        return {
          value: item.id as number,
          label: `#${item.id} · ${name}${date ? ` · ${date}` : ''}`,
        };
      });
    }
    if (spec.lookup === 'indexDateRanges') {
      // Always shown by their Arabic title.
      return list.map((item) => ({
        value: item.id as number,
        label: String(item.titleAr || item.titleEn || item.label || item.id),
      }));
    }
    return list.map((item) => ({
      value: item.id as number,
      label: itemTitle(item),
    }));
  };

  return { isLookupLoading, isWaitingOnParent, paramOptions };
};
